import { useEffect, useState } from "react";

type Size = "sm" | "md" | "lg";

const SIZE_SUFFIX: Record<Size, "sm" | "md" | "1x"> = {
	sm: "sm",
	md: "md",
	lg: "1x",
};

const FALLBACK_TEXT_CLASS: Record<Size, string> = {
	sm: "text-[11px]",
	md: "text-lg",
	lg: "text-2xl",
};

interface ClassHeadshotProps {
	slug: string;
	name: string;
	themeColorHex: string;
	size: Size;
	/** Optional size override. If not provided, uses size-based defaults. */
	className?: string;
	showBorder?: boolean;
}

const DEFAULT_SIZE_CLASS: Record<Size, string> = {
	sm: "h-8 w-8",
	md: "h-14 w-14",
	lg: "h-24 w-24",
};

export function ClassHeadshot({
	slug,
	name,
	themeColorHex,
	size,
	className,
	showBorder = true,
}: ClassHeadshotProps) {
	const [missing, setMissing] = useState(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset missing state when slug changes
	useEffect(() => {
		setMissing(false);
	}, [slug]);

	const base = `/images/classes/headshots/${slug}`;
	const suffix = SIZE_SUFFIX[size];
	const initials = name.slice(0, 2).toUpperCase();
	const sizeClass = className ?? DEFAULT_SIZE_CLASS[size];
	const borderClass = showBorder ? "border-l-4" : "";
	const borderStyle = showBorder
		? { borderLeftColor: themeColorHex }
		: undefined;

	if (missing) {
		return (
			<span
				role="img"
				className={`inline-grid place-items-center bg-surface-container-low font-headline font-black leading-none text-on-surface uppercase ${sizeClass} ${FALLBACK_TEXT_CLASS[size]} ${borderClass}`}
				style={borderStyle}
				aria-label={name}
			>
				{initials}
			</span>
		);
	}

	return (
		<picture>
			<source
				type="image/avif"
				srcSet={`${base}-${suffix}.avif 1x, ${base}-1x.avif 2x`}
			/>
			<source
				type="image/webp"
				srcSet={`${base}-${suffix}.webp 1x, ${base}-1x.webp 2x`}
			/>
			<img
				src={`${base}-${suffix}.webp`}
				alt={name}
				className={`block object-cover ${sizeClass} ${borderClass}`}
				style={borderStyle}
				onError={() => setMissing(true)}
			/>
		</picture>
	);
}
